import { useMemo } from "react";
import {
  DatePicker,
  Descriptions,
  Input,
  Select,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import useIntl from "react-intl/lib/src/components/useIntl";
import { Invoice, InvoiceBody, InvoiceStatus } from "@lib/types/Billing";
import TableIdButton from "../Buttons/TableIdButton";
import dayjs from "dayjs";
import { apiGetCustomers } from "@lib/api/customer";
import { useQuery } from "@tanstack/react-query";
import EditableText from "../EditableText";
import { useTypedSelector } from "@lib/redux";
import { shortenString } from "@lib/helpers/StringHelper";
import { StyledInvoiceInfo } from "./index.styled";
import { Customer } from "@lib/types/Customer";
import { invoiceStatusColor } from "@lib/helpers/ColorHelper";
import { UseMutationProps } from "@lib/types/Api";
import CustomerName from "../Others/CustomerName";
import { StyledFlex } from "../Table/index.styled";

type Props = {
  invoice: Invoice;
  requestBody: InvoiceBody;
  handleRequestBody: (props: Partial<InvoiceBody>) => void;
  customer?: Customer;
  updateQuery: UseMutationProps<Partial<Invoice>>;
  isEditMode: boolean;
};

export default function InvoiceInfo(props: Props) {
  const { invoice, requestBody, handleRequestBody, updateQuery, isEditMode } =
    props;
  const { user } = useTypedSelector((state) => state.auth);
  const { messages } = useIntl();
  const customer = invoice?.customer || props?.customer;

  const dueDateValue = useMemo(() => {
    return !!requestBody?.dueDate
      ? dayjs(requestBody?.dueDate)
      : dayjs()
          .add(dayjs.duration({ days: 7 }))
          .hour(23)
          .minute(50);
  }, [requestBody]);

  const { data: customers, isLoading: customersIsLoading } = useQuery({
    queryFn: () => apiGetCustomers({ limit: 100 }),
    queryKey: ["customers-dropdown"],
    enabled: !customer && isEditMode,
  });

  const handleChangeDueDate = (value: number, save: boolean = false) => {
    if (!isEditMode) return;
    const dueDate = dayjs(value).format();
    handleRequestBody({ dueDate });
    if (save) {
      updateQuery.mutate({ id: invoice.id, dueDate });
    }
  };

  return (
    <StyledInvoiceInfo>
      <Descriptions bordered column={1} size="small" className="custom">
        <Descriptions.Item label={`${messages["common.invoice"] as string} ID`}>
          {invoice?.id ? (
            <TableIdButton
              text={`#${shortenString(invoice?.id) || ""}`}
              textToBeCopied={invoice?.id || ""}
            />
          ) : (
            " - "
          )}
        </Descriptions.Item>
        <Descriptions.Item label={messages["common.status"] as string}>
          {isEditMode ? (
            <>
              {requestBody?.status ? (
                <Select
                  style={{ width: "100%" }}
                  defaultValue={requestBody?.status}
                  onChange={(status) => {
                    updateQuery.mutate({ id: invoice.id, status });
                  }}
                  loading={updateQuery.isPending}
                  optionFilterProp="text"
                  options={Object.values(InvoiceStatus).map((value) => ({
                    label: (
                      <Tag color={invoiceStatusColor(value)}>
                        {messages["enums.status"][value] || value}
                      </Tag>
                    ),
                    text: messages["enums.status"][value] || value,
                    value,
                  }))}
                />
              ) : (
                <Tag color={invoiceStatusColor(requestBody?.status)}>
                  {messages["enums.status"]["Preparing"]}
                </Tag>
              )}
            </>
          ) : (
            <Tag color={invoiceStatusColor(invoice?.status)}>
              {messages["enums.status"][invoice?.status] || invoice?.status}
            </Tag>
          )}
        </Descriptions.Item>
        <Descriptions.Item
          label={messages["common.description"] as string}
          style={{ maxWidth: "400px" }}
        >
          {isEditMode ? (
            <EditableText
              text={requestBody?.description}
              editComponent={
                <Input.TextArea
                  autoFocus
                  value={requestBody?.description}
                  onInput={({ target }) => {
                    handleRequestBody({ description: (target as any).value });
                  }}
                />
              }
            />
          ) : (
            <Typography.Text>{invoice?.description}</Typography.Text>
          )}
        </Descriptions.Item>
      </Descriptions>

      <Descriptions bordered column={1} size="small" className="custom">
        <Descriptions.Item
          label={messages["common.customer"] as string}
          className={`${
            !requestBody?.customerId &&
            !customers?.data?.data?.find(
              (c) => c.id === requestBody?.customerId,
            )?.name &&
            isEditMode
              ? "warning"
              : ""
          }`}
        >
          {isEditMode ? (
            <>
              {customer ? (
                <CustomerName customer={customer} />
              ) : (
                <EditableText
                  text={
                    <CustomerName
                      customer={customers?.data?.data?.find(
                        (c) => c.id === requestBody?.customerId,
                      )}
                    />
                  }
                  forceEdit={
                    !customers?.data?.data?.find(
                      (c) => c.id === requestBody?.customerId,
                    )?.name
                  }
                  editComponent={
                    <Select
                      showSearch
                      loading={customersIsLoading}
                      style={{ minWidth: "150px" }}
                      value={requestBody?.customerId}
                      onChange={(customerId) =>
                        handleRequestBody({ customerId })
                      }
                      options={customers?.data?.data?.map((customer) => ({
                        label: <CustomerName customer={customer} />,
                        text: customer.name,
                        value: customer.id,
                      }))}
                      optionFilterProp="text"
                    />
                  }
                />
              )}
            </>
          ) : (
            <CustomerName customer={customer} />
          )}
        </Descriptions.Item>
        <Descriptions.Item label={messages["common.createdBy"] as string}>
          {invoice ? invoice?.createdBy : user?.fullName}
        </Descriptions.Item>
        <Descriptions.Item label={messages["common.createdAt"] as string}>
          {invoice
            ? dayjs(invoice?.createdAt).format("DD/MM/YYYY HH:mm")
            : dayjs().format("DD/MM/YYYY HH:mm")}
        </Descriptions.Item>
        <Descriptions.Item
          label={messages["common.dueDate"] as string}
          className={`${!requestBody?.dueDate && isEditMode ? "warning" : ""}`}
        >
          {invoice && (
            <StyledFlex className="due-date-print">
              {dayjs(dueDateValue).format("DD/MM/YYYY HH:mm")}
            </StyledFlex>
          )}
          <StyledFlex className="due-date">
            {isEditMode ? (
              <EditableText
                text={
                  <Tooltip
                    title={dayjs(dueDateValue).format("DD/MM/YYYY HH:mm")}
                  >
                    {dayjs(dueDateValue).fromNow()}
                  </Tooltip>
                }
                forceEdit={!requestBody?.dueDate}
                saveOnClick={() => {
                  handleChangeDueDate(dayjs(dueDateValue).valueOf(), true);
                }}
                editComponent={
                  <DatePicker
                    showTime
                    hourStep={1}
                    minuteStep={10}
                    allowClear
                    format={"YYYY-MM-DD HH:mm"}
                    value={dueDateValue}
                    needConfirm={false}
                    onChange={(value) => {
                      console.log("changed");
                      if (value) {
                        handleChangeDueDate(dayjs(value).valueOf());
                      }
                    }}
                    onClick={(e) => {
                      console.log("clicked");
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                  />
                }
              />
            ) : (
              <Tooltip title={dayjs(dueDateValue).format("DD/MM/YYYY HH:mm")}>
                {dayjs(dueDateValue).fromNow()}
              </Tooltip>
            )}
          </StyledFlex>
        </Descriptions.Item>
      </Descriptions>
    </StyledInvoiceInfo>
  );
}
